import { useRedirectToQuery } from '@booking-portal/api/queries/use-redirect-to-query';
import { useSentry } from '@booking-portal/composables/use-sentry';
import { navigationNames } from '@booking-portal/router/router-constants';
import {
  isExternalRedirectInProgress,
  performExternalRedirect,
} from '@booking-portal/utils/external-redirect';
import { computed, type MaybeRefOrGetter, ref, toValue, watch } from 'vue';
import { useRouter } from 'vue-router';

const allowedProtocols = ['http:', 'https:'];

export type IRedirectUrlValidation =
  | { status: 'same-origin' }
  | { status: 'external'; parsedUrl: URL }
  | { status: 'invalid-protocol'; protocol: string }
  | { status: 'parse-error'; error: unknown };

export function validateRedirectUrl(
  redirectUrl: string
): IRedirectUrlValidation {
  try {
    const parsedUrl = new URL(redirectUrl);
    const protocol = parsedUrl.protocol?.toLowerCase() ?? '';

    if (!allowedProtocols.includes(protocol)) {
      return { status: 'invalid-protocol', protocol };
    }

    if (parsedUrl.origin !== window.location.origin) {
      return { status: 'external', parsedUrl };
    }

    return { status: 'same-origin' };
  } catch (error) {
    return { status: 'parse-error', error };
  }
}

export function useBookingPortalCustomDomainRedirect() {
  const sentry = useSentry();
  const router = useRouter();

  const { isLoading, data, error } = useRedirectToQuery();
  const isOperationLoading = ref(false);
  const finalIsLoading = computed(
    () => isLoading.value || isOperationLoading.value
  );

  function redirectToErrorPage() {
    return router.push({ name: navigationNames.errorInvalidDomain });
  }

  watch(error, async () => {
    if (error.value == null) return;
    // Safari cancels in-flight requests when an external redirect starts;
    // those AbortErrors are not real failures and must not redirect us to
    // the invalid-domain page (which would override the external nav).
    if (isExternalRedirectInProgress.value) return;

    sentry.captureException(error.value, {
      tags: { feature: 'custom-domain-redirect' },
    });
    await redirectToErrorPage();
  });

  watch(
    () => data.value?.data?.redirectUrl,
    async (redirectUrl) => {
      if (redirectUrl != null) {
        isOperationLoading.value = true;

        const result = validateRedirectUrl(redirectUrl);

        if (result.status === 'invalid-protocol') {
          sentry.captureException(new Error('Invalid protocol'), {
            extra: {
              protocol: result.protocol,
              redirectUrl: redirectUrl,
            },
            tags: { feature: 'custom-domain-redirect' },
          });

          await redirectToErrorPage();
          isOperationLoading.value = false;
          return;
        }

        if (result.status === 'parse-error') {
          sentry.captureException(result.error, {
            extra: {
              invalidRedirectUrl: redirectUrl,
              currentHostname: window.location.hostname,
            },
            tags: { feature: 'custom-domain-redirect' },
          });

          await redirectToErrorPage();
          isOperationLoading.value = false;
          return;
        }

        if (result.status === 'external') {
          const targetHref = new URL(
            window.location.pathname + window.location.search,
            result.parsedUrl.origin
          ).href;
          performExternalRedirect(targetHref);
          // intentionally leave isOperationLoading = true to keep the
          // loading state while the browser navigates away
          return;
        }

        isOperationLoading.value = false;
      }
    },
    { immediate: true }
  );

  return {
    isLoading: finalIsLoading,
  };
}

interface ILocationForRedirect {
  id: number;
  customerPortalBaseUrl?: string | null;
}

export function useLocationCustomDomainRedirect(params: {
  isLoading: MaybeRefOrGetter<boolean>;
  location: MaybeRefOrGetter<ILocationForRedirect | undefined>;
}) {
  const sentry = useSentry();
  const state = ref<'loading' | 'not-found' | 'done' | 'error'>('loading');
  const error = ref<unknown>(null);

  watch(
    [() => toValue(params.isLoading), () => toValue(params.location)],
    ([isLoading, location]) => {
      error.value = null;

      if (isLoading) {
        state.value = 'loading';
        return;
      }

      if (location == null) {
        error.value = new Error('Location not found');
        state.value = 'not-found';
        return;
      }

      const redirectUrl = location.customerPortalBaseUrl;

      if (!redirectUrl) {
        state.value = 'done';
        return;
      }

      state.value = 'loading';

      const result = validateRedirectUrl(redirectUrl);

      if (result.status === 'same-origin') {
        state.value = 'done';
      } else if (result.status === 'external') {
        performExternalRedirect(redirectUrl);
        // intentionally leave state = 'loading' to keep the loading state
        // while the browser navigates away (prevents content flash)
      } else {
        const err =
          result.status === 'invalid-protocol'
            ? new Error('Invalid redirect URL protocol')
            : result.error;
        sentry.captureException(err, {
          extra: {
            locationId: location.id,
            customerPortalBaseUrl: redirectUrl,
          },
          tags: { feature: 'custom-domain-redirect' },
        });
        error.value = err;
        state.value = 'error';
      }
    },
    { immediate: true }
  );

  return { state, error };
}
